import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, CalendarCheck, MessageCircle, Star } from "lucide-react";
import { BOOKING_URL, IMG, waUrl } from "../config";
import { trackCta, withAttribution } from "../attribution";

const lineAnim = (i) => ({
    initial: { y: "115%" },
    animate: { y: "0%" },
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = ({ t }) => {
    const { scrollY } = useScroll();
    const yBg = useTransform(scrollY, [0, 800], [0, 220]);
    const yText = useTransform(scrollY, [0, 800], [0, -90]);
    const opacity = useTransform(scrollY, [0, 500], [1, 0.15]);

    return (
        <section id="top" data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#1b0b06]">
            <motion.img
                src={IMG.hero}
                alt="Copacabana, Rio de Janeiro"
                style={{ y: yBg }}
                className="absolute inset-0 h-[120%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1b0b06] via-[#1b0b06]/45 to-[#1b0b06]/25" />
            <div className="absolute inset-0 bg-[#f23218]/15 mix-blend-multiply" />
            <div className="grain absolute inset-0" />

            <motion.div style={{ y: yText, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-32 sm:px-6 sm:pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.7 }}
                    data-testid="hero-kicker"
                    className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-[#faff00]"
                >
                    <span className="inline-block h-px w-10 bg-[#faff00]" />
                    {t.hero.kicker}
                </motion.p>

                <h1 data-testid="hero-title" className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#fbf3e8] sm:text-5xl lg:text-6xl">
                    {t.hero.lines.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span className="block" {...lineAnim(i)}>
                                {i === 2 ? (
                                    <span className="italic text-[#f23218] drop-shadow-[0_2px_0_rgba(0,0,0,0.4)]">{line}</span>
                                ) : (
                                    line
                                )}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.7 }}
                    data-testid="hero-subtitle"
                    className="mt-6 max-w-xl text-sm leading-relaxed text-[#fbf3e8]/85 sm:text-base"
                >
                    {t.hero.sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.7 }}
                    className="mt-8 flex flex-wrap items-center gap-3"
                >
                    <a
                        href={withAttribution(BOOKING_URL)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={trackCta("book_now_click", "hero")}
                        data-testid="hero-book-btn"
                        className="group flex items-center gap-2 rounded-full bg-[#f23218] px-7 py-3.5 text-sm font-extrabold uppercase tracking-wider text-white shadow-[0_8px_30px_rgba(242,50,24,0.45)] transition-transform hover:scale-105"
                    >
                        <CalendarCheck size={18} className="transition-transform group-hover:-rotate-6" />
                        {t.hero.book}
                    </a>
                    <a
                        href={waUrl(t.waMsg)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-whatsapp-btn"
                        onClick={trackCta("whatsapp_click", "hero")}
                        className="flex items-center gap-2 rounded-full border border-[#fbf3e8]/40 px-7 py-3.5 text-sm font-extrabold uppercase tracking-wider text-[#fbf3e8] backdrop-blur-sm transition-colors hover:border-[#25d366] hover:bg-[#25d366]/15"
                    >
                        <MessageCircle size={18} />
                        {t.hero.whats}
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    data-testid="hero-rating-badge"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#fbf3e8]/20 bg-[#1b0b06]/50 px-4 py-2 backdrop-blur-sm"
                >
                    <div className="flex gap-0.5 text-[#faff00]">
                        {[...Array(5)].map((_, i) => (
                            <Star key={i} size={13} fill="currentColor" strokeWidth={0} />
                        ))}
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#fbf3e8]/90">{t.hero.rating}</span>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="absolute bottom-6 right-6 z-10 hidden flex-col items-center gap-2 text-[#fbf3e8]/60 sm:flex"
            >
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] [writing-mode:vertical-rl]">{t.hero.scroll}</span>
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                    <ArrowDown size={16} />
                </motion.span>
            </motion.div>
        </section>
    );
};
